import { useEffect, useState } from 'react'
import { api } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'

const TIERS = ['free', 'plus', 'researcher']
const TIER_COLORS = { free: '#6b7280', plus: '#2d4a3e', researcher: '#c8a56e' }

function StatCard({ label, value, sub }) {
  const shown = value != null ? Number(value).toLocaleString() : '-'
  return (
    <div className="admin-stat">
      <div className="admin-stat__value">{shown}</div>
      <div className="admin-stat__label">{label}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>{sub}</div>}
    </div>
  )
}

export default function AdminPage() {
  usePageTitle('Admin')
  const [users, setUsers]   = useState([])
  const [stats, setStats]   = useState(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterTier, setFilterTier] = useState('')
  const [saving, setSaving] = useState(null) // userId being saved

  // Duplicate-persons panel
  const [dupGroups, setDupGroups] = useState([])
  const [dupLoading, setDupLoading] = useState(false)
  const [merging, setMerging] = useState(null) // mergeId in flight

  useEffect(() => {
    // Load independently so a slow/failed stats call doesn't blank the
    // users list (that's the "0 users" bug Ruben kept hitting). Each
    // side keeps its own spinner; the page renders whichever arrives.
    api.get('/admin/users').then(setUsers).catch(err => console.error('users load failed', err))
    api.get('/admin/stats').then(setStats).catch(err => console.error('stats load failed', err))
    setLoading(false)
    loadDuplicates()
  }, [])

  async function loadDuplicates() {
    setDupLoading(true)
    try {
      const data = await api.get('/admin/persons/duplicates')
      setDupGroups(data.groups || [])
    } finally {
      setDupLoading(false)
    }
  }

  async function mergeInto(group, mergeId) {
    if (!confirm('Merge this person into the top row? All relationships + documents move to the kept row; this one is deleted.')) return
    setMerging(mergeId)
    try {
      const result = await api.post('/admin/persons/merge', { keepId: group.suggested_keep_id, mergeId })
      if (!result?.merged) throw new Error('Server returned unexpected response')
      await loadDuplicates()
      // Optimistically remove the merged row + collapse the group if only 1 member remains
      setDupGroups(groups =>
        groups
          .map(g => g.suggested_keep_id === group.suggested_keep_id
            ? { ...g, members: g.members.filter(m => m.id !== mergeId), size: g.size - 1 }
            : g)
          .filter(g => g.members.length >= 2)
      )
    } catch (err) {
      alert('Merge failed: ' + (err?.message || 'unknown'))
    } finally {
      setMerging(null)
    }
  }

  async function updateUser(id, patch) {
    setSaving(id)
    try {
      const updated = await api.patch(`/admin/users/${id}`, patch)
      setUsers(u => u.map(x => x.id === id ? { ...x, ...updated } : x))
    } finally {
      setSaving(null)
    }
  }

  async function deleteUser(user) {
    if (!confirm(`Delete ${user.name} (${user.email})? This cannot be undone.`)) return
    await api.delete(`/admin/users/${user.id}`)
    setUsers(u => u.filter(x => x.id !== user.id))
  }

  // Beta comp: grant 90 days of Researcher, free, auto-reverting.
  async function grantBeta(user) {
    setSaving(user.id)
    try {
      const updated = await api.post(`/admin/users/${user.id}/beta`)
      setUsers(u => u.map(x => x.id === user.id ? { ...x, tier: updated.tier, beta_access_until: updated.beta_access_until } : x))
    } catch (e) {
      alert(e.message || 'Could not grant beta access')
    } finally {
      setSaving(null)
    }
  }
  async function revokeBeta(user) {
    if (!confirm(`Revoke beta access for ${user.email}? They drop to Free now.`)) return
    setSaving(user.id)
    try {
      const updated = await api.delete(`/admin/users/${user.id}/beta`)
      setUsers(u => u.map(x => x.id === user.id ? { ...x, tier: updated.tier, beta_access_until: null } : x))
    } catch (e) {
      alert(e.message || 'Could not revoke')
    } finally {
      setSaving(null)
    }
  }

  const filtered = users.filter(u => {
    const q = search.toLowerCase()
    const matchSearch = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    const matchTier = !filterTier || u.tier === filterTier
    return matchSearch && matchTier
  })

  if (loading) return <div className="loading">Loading admin panel</div>

  return (
    <div>
      <div className="page-header">
        <div className="page-header__top">
          <div>
            <div className="page-header__label">Admin</div>
            <h1 className="page-header__title">User Management</h1>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 2.5rem 2.5rem' }}>

        {/* Stats */}
        {stats && (
          <div className="admin-stats">
            <StatCard label="Total users"    value={stats.total_users} />
            <StatCard label="Active"         value={stats.active_users} />
            <StatCard label="Paid"           value={stats.paid_users} />
            <StatCard label="New this week"  value={stats.new_this_week} />
            <StatCard label="Trees"          value={stats.total_trees} />
            <StatCard label="Persons in trees" value={stats.total_persons} />
            <StatCard
              label="KG records"
              value={stats.records_total}
              sub={stats.records_wikidata != null
                ? [
                    `${stats.records_wikidata.toLocaleString()} Wikidata`,
                    `${stats.records_gedcom.toLocaleString()} GEDCOM`,
                    stats.records_user > 0 && `${stats.records_user.toLocaleString()} user`,
                  ].filter(Boolean).join(' · ')
                : null}
            />
          </div>
        )}

        {/* Filters */}
        <div className="admin-filters">
          <input
            className="form-input"
            style={{ maxWidth: 280 }}
            placeholder="Search name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="form-input form-input--select"
            style={{ maxWidth: 160 }}
            value={filterTier}
            onChange={e => setFilterTier(e.target.value)}
          >
            <option value="">All tiers</option>
            {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {filtered.length} user{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Table */}
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Tier</th>
                <th>Role</th>
                <th>Trees</th>
                <th>Persons</th>
                <th>Last login</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(user => (
                <tr key={user.id} style={{ opacity: user.is_active ? 1 : 0.45 }}>
                  <td>
                    <div className="admin-user-name">{user.name}</div>
                    <div className="admin-user-email">{user.email}</div>
                  </td>

                  <td>
                    <select
                      className="admin-select"
                      value={user.tier}
                      disabled={saving === user.id}
                      onChange={e => updateUser(user.id, { tier: e.target.value })}
                      style={{ color: TIER_COLORS[user.tier] }}
                    >
                      {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {user.beta_access_until && new Date(user.beta_access_until) > new Date() ? (
                      <div style={{ fontSize: '0.66rem', color: '#a8843e', marginTop: 3, display: 'flex', gap: 4, alignItems: 'center' }}>
                        🎁 beta · {new Date(user.beta_access_until).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        <button
                          onClick={() => revokeBeta(user)}
                          disabled={saving === user.id}
                          title="Revoke beta now"
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, lineHeight: 1 }}
                        >×</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => grantBeta(user)}
                        disabled={saving === user.id}
                        title="Grant 90 days of free Researcher (auto-reverts)"
                        style={{ marginTop: 3, fontSize: '0.66rem', padding: '1px 6px', cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 5, background: '#fff', color: '#a8843e' }}
                      >🎁 90-day beta</button>
                    )}
                  </td>

                  <td>
                    <select
                      className="admin-select"
                      value={user.role}
                      disabled={saving === user.id}
                      onChange={e => updateUser(user.id, { role: e.target.value })}
                    >
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>

                  <td style={{ textAlign: 'center' }}>{user.tree_count}</td>
                  <td style={{ textAlign: 'center' }}>{user.person_count}</td>

                  <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {user.last_login
                      ? new Date(user.last_login).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
                      : 'Never'}
                  </td>

                  <td>
                    <button
                      className={`admin-toggle ${user.is_active ? 'admin-toggle--active' : 'admin-toggle--inactive'}`}
                      disabled={saving === user.id}
                      onClick={() => updateUser(user.id, { is_active: !user.is_active })}
                    >
                      {user.is_active ? 'Active' : 'Disabled'}
                    </button>
                  </td>

                  <td>
                    <button
                      className="admin-delete"
                      disabled={saving === user.id}
                      onClick={() => deleteUser(user)}
                      title="Delete user"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Duplicate persons */}
        <div style={{ marginTop: '2.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h2 style={{ fontSize: '1.05rem', fontWeight: 600 }}>Duplicate persons</h2>
            <button
              className="btn btn-ghost btn-sm"
              onClick={loadDuplicates}
              disabled={dupLoading}
              style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}
            >
              {dupLoading ? 'Scanning…' : 'Rescan'}
            </button>
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Groups of ≥2 persons sharing first name + last name + birth year. The first row is kept; click <em>Merge into top</em> on any other row to collapse it in — relationships, documents, and photos move to the kept row, and any missing field on the kept row is backfilled from the merged one.
          </p>
          {dupGroups.length === 0 ? (
            <div style={{ padding: '1.5rem', border: '1px dashed var(--border)', borderRadius: 10, color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center' }}>
              {dupLoading ? 'Scanning…' : 'No duplicate groups found.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {dupGroups.map(g => (
                <div key={g.suggested_keep_id} style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'white', overflow: 'hidden' }}>
                  <div style={{ padding: '0.5rem 0.9rem', background: 'var(--paper)', fontSize: '0.75rem', color: 'var(--text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {g.members[0].first_name} {g.members[0].last_name} · {g.size} copies
                  </div>
                  <table className="admin-table" style={{ margin: 0 }}>
                    <tbody>
                      {g.members.map((m, i) => {
                        const isKeep = m.id === g.suggested_keep_id
                        const by = m.birth_date ? String(m.birth_date).match(/\d{4}/)?.[0] : null
                        const dy = m.death_date ? String(m.death_date).match(/\d{4}/)?.[0] : null
                        return (
                          <tr key={m.id}>
                            <td style={{ width: 12, background: isKeep ? 'color-mix(in srgb, var(--green) 12%, white)' : 'transparent' }} />
                            <td>
                              <div style={{ fontSize: '0.9rem' }}>
                                {m.first_name || '—'} {m.last_name || ''}
                                {isKeep && <span style={{ marginLeft: 8, fontSize: '0.7rem', color: 'var(--green)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Keep</span>}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{m.id}</div>
                            </td>
                            <td style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                              {by ? (dy ? `${by} – ${dy}` : `b. ${by}`) : '—'}
                            </td>
                            <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                              added {m.created_at ? new Date(m.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {!isKeep && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  disabled={merging === m.id}
                                  onClick={() => mergeInto(g, m.id)}
                                  style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}
                                >
                                  {merging === m.id ? 'Merging…' : 'Merge into top'}
                                </button>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        <CapsuleAdmin />
        <ChangelogAdmin />
      </div>
    </div>
  )
}

// ── Changelog authoring ──────────────────────────────────────────────────────
// Create + publish user-facing "What's new" entries. Drafts (published_at NULL)
// stay hidden until published; the public /changelog page shows published only.
function ChangelogAdmin() {
  const empty = { title: '', body: '', tag: '' }
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState(empty)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  async function load() {
    setLoading(true)
    try { setList(await api.get('/admin/changelog')) }
    catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  function startEdit(entry) {
    setEditingId(entry.id)
    setForm({ title: entry.title || '', body: entry.body || '', tag: entry.tag || '' })
    setError(null)
  }
  function cancelEdit() { setEditingId(null); setForm(empty); setError(null) }

  async function save(publish) {
    if (!form.title.trim()) { setError('A title is required'); return }
    setSaving(true); setError(null)
    try {
      if (editingId) {
        await api.patch(`/admin/changelog/${editingId}`, {
          title: form.title, body: form.body, tag: form.tag || null,
          ...(publish !== undefined ? { published: publish } : {}),
        })
      } else {
        await api.post('/admin/changelog', {
          title: form.title, body: form.body, tag: form.tag || null, publish: !!publish,
        })
      }
      setForm(empty); setEditingId(null)
      await load()
    } catch (e) { setError(e.message) }
    finally { setSaving(false) }
  }

  async function togglePublish(entry) {
    await api.patch(`/admin/changelog/${entry.id}`, { published: !entry.published_at }).catch(e => setError(e.message))
    load()
  }
  async function remove(entry) {
    if (!confirm(`Delete "${entry.title}"?`)) return
    await api.delete(`/admin/changelog/${entry.id}`).catch(e => setError(e.message))
    load()
  }

  return (
    <div className="admin-section" style={{ marginTop: '2rem' }}>
      <h2 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.75rem' }}>
        Changelog — “What’s new”
      </h2>

      <div className="changelog-admin__form">
        <input
          className="admin-input"
          placeholder="Title — e.g. Pull relatives from the Knowledge Graph"
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        />
        <textarea
          className="admin-input"
          rows={3}
          placeholder="What it means for the user. One line per paragraph."
          value={form.body}
          onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
        />
        <div className="changelog-admin__row">
          <select
            className="admin-select"
            value={form.tag}
            onChange={e => setForm(f => ({ ...f, tag: e.target.value }))}
          >
            <option value="">No tag</option>
            <option value="new">New</option>
            <option value="improved">Improved</option>
            <option value="fixed">Fixed</option>
          </select>
          <div style={{ flex: 1 }} />
          {editingId && (
            <button className="btn btn-ghost btn-sm" onClick={cancelEdit} disabled={saving}>Cancel</button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => save(undefined)} disabled={saving}>
            {editingId ? 'Save' : 'Save draft'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => save(true)} disabled={saving}>
            {editingId ? 'Save & publish' : 'Publish'}
          </button>
        </div>
        {error && <div className="changelog-admin__error">{error}</div>}
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', padding: '0.75rem 0' }}>Loading…</div>
      ) : list.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', padding: '0.75rem 0' }}>No entries yet.</div>
      ) : (
        <table className="admin-table" style={{ marginTop: '1rem' }}>
          <tbody>
            {list.map(e => (
              <tr key={e.id}>
                <td>
                  <div style={{ fontWeight: 600 }}>
                    {e.title}{' '}
                    {e.tag && <span className={`changelog-admin__tag tag-${e.tag}`}>{e.tag}</span>}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {e.published_at
                      ? `Published ${new Date(e.published_at).toLocaleDateString()}`
                      : 'Draft'}
                  </div>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => startEdit(e)}>Edit</button>{' '}
                  <button className="btn btn-ghost btn-sm" onClick={() => togglePublish(e)}>
                    {e.published_at ? 'Unpublish' : 'Publish'}
                  </button>{' '}
                  <button className="admin-delete" onClick={() => remove(e)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── Capsule author + review panel ────────────────────────────────────────────
// Curator flow: fill a short brief, POST /admin/capsules/author, Claude
// returns a JSON draft saved with is_verified=false. Draft appears in the
// list below where the curator can preview (via the user-facing route),
// publish (flip is_verified=true), or delete.
function CapsuleAdmin() {
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(false)
  const [brief, setBrief] = useState({
    theme: 'war',
    country: 'NL',
    region: '',
    place: '',
    start_year: 1940,
    end_year: 1945,
    demographic_tags: '',
    kickoff_notes: '',
  })
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError] = useState(null)
  const [expanded, setExpanded] = useState(null)

  async function load() {
    setLoading(true)
    try {
      const all = await api.get('/admin/capsules')
      setList(all || [])
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  async function generate(e) {
    e.preventDefault()
    setGenError(null)
    setGenerating(true)
    try {
      const body = {
        ...brief,
        start_year: parseInt(brief.start_year, 10),
        end_year: parseInt(brief.end_year, 10),
        demographic_tags: brief.demographic_tags
          .split(',').map(t => t.trim()).filter(Boolean),
      }
      await api.post('/admin/capsules/author', body)
      await load()
    } catch (err) {
      setGenError(err.message || 'Generation failed')
    } finally {
      setGenerating(false)
    }
  }

  async function verify(id) {
    await api.post(`/admin/capsules/${id}/verify`, {})
    await load()
  }
  async function remove(id) {
    if (!confirm('Delete this capsule permanently?')) return
    await api.delete(`/admin/capsules/${id}`)
    await load()
  }

  return (
    <div className="admin-section" style={{ marginTop: '2.5rem' }}>
      <h3 style={{ marginBottom: '1rem' }}>Life capsules</h3>

      <form
        onSubmit={generate}
        style={{
          display: 'grid', gap: '0.75rem',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          padding: '1rem', border: '1px solid var(--border)',
          borderRadius: '8px', background: 'var(--surface, #fafaf8)',
          marginBottom: '1rem',
        }}
      >
        <label>
          <span style={labelStyle}>Theme</span>
          <select
            className="form-input"
            value={brief.theme}
            onChange={e => setBrief({ ...brief, theme: e.target.value })}
          >
            {['war','childhood','work','migration','epidemic','flood','other'].map(t =>
              <option key={t}>{t}</option>
            )}
          </select>
        </label>
        <label>
          <span style={labelStyle}>Country (ISO-2, blank = global)</span>
          <input className="form-input" value={brief.country} onChange={e => setBrief({ ...brief, country: e.target.value })} />
        </label>
        <label>
          <span style={labelStyle}>Region</span>
          <input className="form-input" value={brief.region} onChange={e => setBrief({ ...brief, region: e.target.value })} placeholder="e.g. Zeeland" />
        </label>
        <label>
          <span style={labelStyle}>Place</span>
          <input className="form-input" value={brief.place} onChange={e => setBrief({ ...brief, place: e.target.value })} placeholder="e.g. Rotterdam" />
        </label>
        <label>
          <span style={labelStyle}>Start year</span>
          <input className="form-input" type="number" value={brief.start_year} onChange={e => setBrief({ ...brief, start_year: e.target.value })} />
        </label>
        <label>
          <span style={labelStyle}>End year</span>
          <input className="form-input" type="number" value={brief.end_year} onChange={e => setBrief({ ...brief, end_year: e.target.value })} />
        </label>
        <label style={{ gridColumn: '1 / -1' }}>
          <span style={labelStyle}>Demographic tags (comma-separated)</span>
          <input className="form-input" value={brief.demographic_tags} onChange={e => setBrief({ ...brief, demographic_tags: e.target.value })} placeholder="urban, working_class, wartime_adult" />
        </label>
        <label style={{ gridColumn: '1 / -1' }}>
          <span style={labelStyle}>Kickoff notes (optional)</span>
          <textarea
            className="form-input"
            rows={3}
            value={brief.kickoff_notes}
            onChange={e => setBrief({ ...brief, kickoff_notes: e.target.value })}
            placeholder="Anything specific you want included — e.g. 'emphasise the Hunger Winter food convoys', 'this is for shopkeepers not dockworkers'"
          />
        </label>
        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          <button type="submit" className="btn btn-primary btn-sm" disabled={generating}>
            {generating ? 'Generating… (~20s)' : 'Generate draft'}
          </button>
          {genError && <span style={{ color: '#b45309', fontSize: '0.85rem' }}>{genError}</span>}
        </div>
      </form>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          {list.map(c => (
            <div
              key={c.id}
              style={{
                padding: '0.85rem 1rem',
                borderBottom: '1px solid var(--border)',
                background: c.is_verified ? 'white' : 'color-mix(in srgb, var(--gold) 6%, white)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '1rem' }}>{c.title}</strong>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  {c.start_year}{c.end_year !== c.start_year && `–${c.end_year}`}
                  {c.place && ` · ${c.place}`}
                  {c.region && !c.place && ` · ${c.region}`}
                  {` · ${c.theme}`}
                </span>
                <span style={{
                  fontSize: '0.7rem', padding: '0.1rem 0.45rem', borderRadius: '99px',
                  background: c.is_verified ? 'var(--green)' : 'var(--gold)',
                  color: '#fff', fontWeight: 600,
                }}>
                  {c.is_verified ? 'Published' : 'Draft'}
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(expanded === c.id ? null : c.id)}>
                    {expanded === c.id ? 'Hide' : 'Details'}
                  </button>
                  {!c.is_verified && (
                    <button className="btn btn-primary btn-sm" onClick={() => verify(c.id)}>Publish</button>
                  )}
                  <button className="btn btn-ghost btn-sm" style={{ color: '#991b1b' }} onClick={() => remove(c.id)}>Delete</button>
                </div>
              </div>
              {c.tagline && <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>{c.tagline}</div>}
              {expanded === c.id && (
                <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  <div>slug: <code>{c.slug}</code></div>
                  <div>tags: {(c.demographic_tags || []).join(', ') || '—'}</div>
                  <div>created: {new Date(c.created_at).toLocaleString()}</div>
                  <div style={{ marginTop: '0.4rem' }}>
                    Full content JSON is in the <code>life_capsules</code> table — edit directly in the DB if needed before publishing.
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <HarvestsPanel />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// HarvestsPanel — kick off and monitor Knowledge Graph ingestion jobs.
//
// Source list comes from GET /api/admin/harvests/sources so adding a new
// source is backend-only (no UI edit needed). Form fields are rendered
// dynamically from `params[]` on the source definition.
// ─────────────────────────────────────────────────────────────────────────────
function HarvestsPanel() {
  const [sources, setSources] = useState([])
  const [sourceId, setSourceId] = useState('wikidata')
  const [formValues, setFormValues] = useState({})
  const [runs, setRuns] = useState([])
  const [activeRunId, setActiveRunId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    api.get('/admin/harvests/sources').then(d => {
      const list = d.sources || []
      setSources(list)
      // Seed defaults for the first active source's params
      const active = list.find(s => s.active)
      if (active) {
        setSourceId(active.id)
        setFormValues(paramDefaults(active))
      }
    }).catch(() => {})
  }, [])

  async function loadRuns() {
    try {
      const data = await api.get('/admin/harvests')
      setRuns(data.runs || [])
      setActiveRunId(data.active_run_id || null)
    } catch {}
  }

  // Poll while anything is running; otherwise a single fetch on mount.
  useEffect(() => {
    loadRuns()
    const interval = activeRunId ? 5000 : 30000
    const id = setInterval(loadRuns, interval)
    return () => clearInterval(id)
  }, [activeRunId])

  function paramDefaults(source) {
    const out = {}
    for (const p of source.params || []) {
      if (p.default !== undefined) out[p.name] = p.default
    }
    return out
  }

  function updateSource(id) {
    setSourceId(id)
    const s = sources.find(x => x.id === id)
    setFormValues(s ? paramDefaults(s) : {})
  }

  const selected = sources.find(s => s.id === sourceId)
  const sourceDisabled = !selected || !selected.active

  async function startHarvest() {
    setError(null)
    setSubmitting(true)
    try {
      const payload = { source: sourceId, params: coerceParams(selected, formValues) }
      await api.post('/admin/harvests', payload)
      await loadRuns()
    } catch (err) {
      setError(err.message || 'Failed to start harvest')
    } finally {
      setSubmitting(false)
    }
  }

  async function cancelRun(id) {
    if (!confirm('Cancel this harvest? It will stop at the next checkpoint (up to ~30s).')) return
    try {
      await api.post(`/admin/harvests/${id}/cancel`, {})
      await loadRuns()
    } catch (err) {
      setError(err.message || 'Cancel failed')
    }
  }

  async function reconcile() {
    try {
      const r = await api.post('/admin/harvests/reconcile', {})
      alert(`Reconciled ${r.reconciled} orphan run(s).`)
      await loadRuns()
    } catch (err) {
      setError(err.message || 'Reconcile failed')
    }
  }

  return (
    <div style={{ marginTop: '2.5rem' }}>
      <h2 style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>Harvests — Knowledge Graph ingestion</h2>

      {/* Start form */}
      <div style={{ background: 'var(--paper)', border: '1px solid var(--border)', borderRadius: 10, padding: '1.2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.85rem' }}>
          <div>
            <label style={labelStyle}>Source</label>
            <select
              value={sourceId}
              onChange={e => updateSource(e.target.value)}
              style={selectStyle}
              disabled={sources.length === 0}
            >
              {sources.length === 0 && (
                <option value="">No sources loaded — check API is running</option>
              )}
              {sources.map(s => (
                <option key={s.id} value={s.id} disabled={!s.active}>
                  {s.label}{!s.active && ' — coming soon'}
                </option>
              ))}
            </select>
          </div>

          {selected?.params.map(p => {
            // Boolean param rendered as a checkbox. When the user hasn't
            // explicitly set a value, derive a default from yearFrom (the
            // safety-auto-enable at 1920+). Explicit clicks override.
            const autoDeceased = p.name === 'requireDeceased'
              && formValues.requireDeceased === undefined
              && Number(formValues.yearFrom) >= 1920
            const boolValue = formValues[p.name] === true || autoDeceased
            if (p.type === 'boolean') {
              return (
                <div key={p.name} style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text)' }}>
                    <input
                      type="checkbox"
                      checked={boolValue}
                      onChange={e => setFormValues(v => ({ ...v, [p.name]: e.target.checked }))}
                      disabled={sourceDisabled}
                      style={{ width: 16, height: 16, accentColor: 'var(--green)' }}
                    />
                    <span>
                      {p.label}
                      {autoDeceased && <span style={{ color: 'var(--gold)', marginLeft: '0.4rem', fontWeight: 600 }}>· auto-on</span>}
                    </span>
                  </label>
                  {p.help && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.25rem', paddingLeft: '1.4rem' }}>
                      {p.help}
                    </div>
                  )}
                </div>
              )
            }
            return (
              <div key={p.name}>
                <label style={labelStyle}>{p.label}{p.required ? ' *' : ''}</label>
                {p.type === 'country' ? (
                  <select
                    value={formValues[p.name] || ''}
                    onChange={e => setFormValues(v => ({ ...v, [p.name]: e.target.value }))}
                    style={selectStyle}
                    disabled={sourceDisabled}
                  >
                    <option value="">— pick —</option>
                    {(p.options || []).map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                ) : p.type === 'year' || p.type === 'number' ? (
                  <input
                    type="number"
                    min={p.min}
                    max={p.max}
                    value={formValues[p.name] ?? ''}
                    onChange={e => setFormValues(v => ({ ...v, [p.name]: e.target.value }))}
                    style={inputStyle}
                    disabled={sourceDisabled}
                  />
                ) : (
                  <input
                    type="text"
                    value={formValues[p.name] || ''}
                    onChange={e => setFormValues(v => ({ ...v, [p.name]: e.target.value }))}
                    style={inputStyle}
                    disabled={sourceDisabled}
                  />
                )}
              </div>
            )
          })}
        </div>

        {selected?.description && (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '0.9rem 0 0' }}>{selected.description}</p>
        )}

        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={startHarvest}
            disabled={sourceDisabled || submitting || !!activeRunId}
          >
            {submitting ? 'Starting…' : activeRunId ? 'Another run in progress' : 'Start harvest'}
          </button>
          {activeRunId && (
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              A run is active. Wait for it to finish, or cancel it below.
            </span>
          )}
        </div>
        {error && <div className="form-error" style={{ marginTop: '0.8rem' }}>{error}</div>}
      </div>

      {/* Recent runs */}
      <div style={{ marginTop: '1.2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <h3 style={{ fontSize: '0.95rem', margin: 0 }}>Recent runs</h3>
          <button className="btn btn-ghost btn-sm" onClick={reconcile}>Reconcile orphan runs</button>
        </div>

        {runs.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No runs yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Params</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>+New</th>
                  <th style={thStyle}>Updated</th>
                  <th style={thStyle}>Failed</th>
                  <th style={thStyle}>Started</th>
                  <th style={thStyle}>Finished</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => {
                  const params = r.query_params || {}
                  const paramsSummary = [
                    params.country,
                    params.yearFrom && params.yearTo ? `${params.yearFrom}–${params.yearTo}` : null,
                  ].filter(Boolean).join(' · ') || '—'
                  const isActive = r.id === activeRunId
                  return (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={tdStyle}>{r.harvester}</td>
                      <td style={tdStyle}>{paramsSummary}</td>
                      <td style={tdStyle}>
                        <span style={statusStyle(r.status)}>
                          {isActive ? '● ' : ''}{r.status}
                        </span>
                      </td>
                      <td style={tdStyle}>{r.records_ingested}</td>
                      <td style={tdStyle}>{r.records_updated}</td>
                      <td style={tdStyle}>{r.records_failed}</td>
                      <td style={tdStyle}>{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</td>
                      <td style={tdStyle}>{r.finished_at ? new Date(r.finished_at).toLocaleString() : '—'}</td>
                      <td style={tdStyle}>
                        {isActive && (
                          <button className="btn btn-ghost btn-sm" style={{ color: '#b54040' }} onClick={() => cancelRun(r.id)}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// Coerce string form values into typed params the backend expects.
function coerceParams(source, values) {
  const out = {}
  for (const p of source?.params || []) {
    const v = values[p.name]
    if (p.type === 'boolean') {
      // Booleans pass through even when unchecked — lets the server
      // distinguish "user explicitly unchecked" from "user didn't touch"
      // for the auto-enable rule. Unchecked sends false; never sent
      // means the backend falls back to its default logic.
      if (v === true || v === false) out[p.name] = v
      continue
    }
    if (v === undefined || v === '' || v === null) continue
    if (p.type === 'year' || p.type === 'number') out[p.name] = Number(v)
    else out[p.name] = v
  }
  return out
}

function statusStyle(status) {
  const colors = {
    running:   { bg: '#fff7e0', fg: '#8a6400' },
    completed: { bg: '#e6f5ec', fg: '#2d5a3e' },
    cancelled: { bg: '#f2f2f2', fg: '#5a5a5a' },
    failed:    { bg: '#fde8e8', fg: '#a01a1a' },
  }[status] || { bg: '#f2f2f2', fg: '#555' }
  return {
    display: 'inline-block',
    padding: '0.12rem 0.5rem',
    borderRadius: 999,
    background: colors.bg,
    color: colors.fg,
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'capitalize',
  }
}

const selectStyle = {
  width: '100%', padding: '0.45rem 0.6rem', borderRadius: 6,
  border: '1px solid var(--border)', fontSize: '0.9rem', background: '#fff',
}
const inputStyle = { ...selectStyle }
const thStyle = { padding: '0.4rem 0.6rem', fontWeight: 600 }
const tdStyle = { padding: '0.5rem 0.6rem', verticalAlign: 'top' }

const labelStyle = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
  marginBottom: '0.25rem',
  textTransform: 'uppercase',
}
